import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { useState } from "react";
import {
  Phone,
  Mail,
  MapPin,
  Search,
  Filter,
  MessageSquare,
  Video,
  Calendar,
  Star,
  Edit,
  Plus,
  Send,
  Loader2,
  ChevronRight,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { BulkCommunicationPanel } from "./BulkCommunicationPanel";
import API from "../api/api01";
import { useEffect } from "react";

interface Contact {
  id: string;
  name: string;
  email: string;
  phone: string;
  alternatePhone?: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  course: string;
  priority: "High" | "Medium" | "Low";
  source: "Website" | "Walk-in" | "Campaign" | "Referral";
  counselor: {
    name: string;
    avatar: string;
  };
  lastContact: string;
  rating: number;
}

export function LeadContact() {
  const [searchQuery, setSearchQuery] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
  const [isBulkCommunicationOpen, setIsBulkCommunicationOpen] = useState(false);
  const [isCommTypeDialogOpen, setIsCommTypeDialogOpen] = useState(false);
  const [selectedCommType, setSelectedCommType] = useState<string>("email");
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState<Contact[]>([]);

  // Form states (Restored)
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editAlternatePhone, setEditAlternatePhone] = useState("");
  const [editAddress, setEditAddress] = useState("");
  const [editCity, setEditCity] = useState("");
  const [editState, setEditState] = useState("");
  const [editPincode, setEditPincode] = useState("");
  const [editCourse, setEditCourse] = useState("");
  const [editPriority, setEditPriority] = useState<"High" | "Medium" | "Low">("Medium");

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const response = await API.get("/lms-contacts/");
      const data = response.data || [];
      
      const mappedContacts: Contact[] = data.map((c: any) => ({
        id: c.id,
        name: c.name,
        email: c.email,
        phone: c.phone || "",
        alternatePhone: "",
        address: c.address || "N/A",
        city: c.city || "N/A",
        state: "N/A",
        pincode: "N/A",
        course: c.course || "N/A",
        priority: (c.priority as any) || "Medium",
        source: (c.source as any) || "Website",
        counselor: {
          name: c.counselor_name || "Unassigned",
          avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${c.id}`
        },
        lastContact: c.last_contact || "Never",
        rating: 0
      }));

      if (mappedContacts.length > 0) {
        toast.success("Contacts loaded");
      } else {
        toast.error("No records found");
      }
      setContacts(mappedContacts);
    } catch (error) {
      console.error("Failed to fetch contacts:", error);
      toast.error("Failed to load contacts from system.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "High":
        return "bg-red-500/10 text-red-500 border-red-500/20";
      case "Medium":
        return "bg-orange-500/10 text-orange-500 border-orange-500/20";
      case "Low":
        return "bg-green-500/10 text-green-500 border-green-500/20";
      default:
        return "";
    }
  };

  const filteredContacts = contacts.filter((contact) => {
    const matchesSearch =
      contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.phone.includes(searchQuery) ||
      contact.city.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesFilter =
      priorityFilter === "all" || contact.priority === priorityFilter;

    return matchesSearch && matchesFilter;
  });

  const handleEditContact = (contact: Contact) => {
    setSelectedContact(contact);
    setEditName(contact.name);
    setEditEmail(contact.email);
    setEditPhone(contact.phone);
    setEditAlternatePhone(contact.alternatePhone || "");
    setEditAddress(contact.address);
    setEditCity(contact.city);
    setEditState(contact.state);
    setEditPincode(contact.pincode);
    setEditCourse(contact.course);
    setEditPriority(contact.priority);
    setIsEditDialogOpen(true);
  };

  const [isMessageDialogOpen, setIsMessageDialogOpen] = useState(false);
const [selectedLead, setSelectedLead] = useState<Contact | null>(null);
const [messageText, setMessageText] = useState("");


  const handleSaveEdit = async () => {
    if (!selectedContact) return;

    try {
      const updatedData = {
        name: editName,
        email: editEmail,
        phone: editPhone,
        alternate_phone: editAlternatePhone,
        address: editAddress,
        city: editCity,
        state: editState,
        pincode: editPincode,
        course: editCourse,
        priority: editPriority,
      };

      await API.put(`/lms-contacts/${selectedContact.id}`, updatedData);
      
      toast.success("Contact updated successfully!");
      setIsEditDialogOpen(false);
      fetchContacts();
    } catch (error: any) {
      console.error("Failed to update contact:", error);
      const detail = error.response?.data?.detail;
      const errorMessage = typeof detail === 'string' 
        ? detail 
        : Array.isArray(detail) 
          ? detail.map((d: any) => `${d.loc.join('.')}: ${d.msg}`).join(', ')
          : "Failed to update contact.";
      toast.error(errorMessage);
    }
  };

  const handleSelectContact = (contactId: string, checked: boolean) => {
    if (checked) {
      setSelectedContactIds((prev) => [...prev, contactId]);
    } else {
      setSelectedContactIds((prev) => prev.filter((id) => id !== contactId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedContactIds(filteredContacts.map((c) => c.id));
    } else {
      setSelectedContactIds([]);
    }
  };

  const selectedContacts = contacts.filter((c) =>
    selectedContactIds.includes(c.id)
  );

  const handleOpenBulkCommunication = () => {
    if (selectedContactIds.length === 0) {
      toast.error("Please select at least one contact");
      return;
    }
    setIsCommTypeDialogOpen(true);
  };

  const handleSelectCommType = (type: string) => {
    setSelectedCommType(type);
    setIsCommTypeDialogOpen(false);
    setIsBulkCommunicationOpen(true);
  };

  if (loading) {
    return (
      <div className="flex h-[80vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6 bg-background min-h-full">
      {/* Header */}
      <div>
        <h1>Lead Contacts</h1>
        <p className="text-muted-foreground mt-1">
          Manage contact information and communication channels
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="shadow-sm bg-card border-border">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-primary/10">
                <Phone className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{contacts.length}</p>
                <p className="text-sm text-muted-foreground">Total Contacts</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm bg-card border-border">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-red-500/10">
                <Star className="h-6 w-6 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {contacts.filter(c => c.priority === "High").length}
                </p>
                <p className="text-sm text-muted-foreground">High Priority</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm bg-card border-border">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-secondary/10">
                <Mail className="h-6 w-6 text-secondary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{contacts.length}</p>
                <p className="text-sm text-muted-foreground">Email Verified</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm bg-card border-border">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-chart-3/10">
                <MapPin className="h-6 w-6 text-chart-3" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {new Set(contacts.map(c => c.city)).size}
                </p>
                <p className="text-sm text-muted-foreground">Cities</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
<Card className="shadow-sm bg-card border-border">
  <CardContent className="p-4">
    <div className="flex flex-col md:flex-row gap-4 items-center">

      {/* Search */}
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search contacts..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 rounded-lg"
        />
      </div>

      {/* Priority Dropdown */}
      <Select value={priorityFilter} onValueChange={setPriorityFilter}>
  <SelectTrigger className="w-[180px] rounded-lg bg-background border-border">
    <SelectValue placeholder="All Priorities" />
  </SelectTrigger>

  <SelectContent className="bg-popover border-border shadow-2xl rounded-xl z-50">
    <SelectItem value="all">All Priorities</SelectItem>
    <SelectItem value="High">High Priority</SelectItem>
    <SelectItem value="Medium">Medium Priority</SelectItem>
    <SelectItem value="Low">Low Priority</SelectItem>
  </SelectContent>
</Select>

            <Button
              onClick={handleOpenBulkCommunication}
              disabled={selectedContactIds.length === 0}
              className="bg-primary hover:bg-primary/90 rounded-lg gap-2 text-white">
              <Send className="h-4 w-4" />
              Bulk Communication
              {selectedContactIds.length > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {selectedContactIds.length}
                </Badge>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Selection Header */}
      {filteredContacts.length > 0 && (
        <div className="flex items-center gap-3 px-2">
          <Checkbox
  checked={
    selectedContactIds.length === filteredContacts.length &&
    filteredContacts.length > 0
  }
  onCheckedChange={handleSelectAll}
  id="select-all"
  className="
    border-2
    data-[state=checked]:bg-primary
    data-[state=checked]:border-primary
    data-[state=checked]:text-white
  "
/>
          <Label htmlFor="select-all" className="text-sm cursor-pointer">
            Select All ({filteredContacts.length} contacts)
          </Label>
        </div>
      )}

      {/* Contact Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredContacts.length === 0 ? (
          <div className="col-span-full py-20 bg-card rounded-2xl border-2 border-dashed border-border flex flex-col items-center justify-center text-slate-400">
            <Users className="h-12 w-12 mb-4 opacity-20" />
            <p className="text-lg font-medium">No records found</p>
            <p className="text-sm">Try adjusting your search or filters</p>
          </div>
        ) : (
          filteredContacts.map((contact) => (
            <Card key={contact.id} className="shadow-sm hover:shadow-md transition-shadow relative bg-card border-border">
              {/* Selection Checkbox */}
              <div className="absolute top-3 left-3 z-10">
                <Checkbox
                  checked={selectedContactIds.includes(contact.id)}
                  onCheckedChange={(checked) => {
                    handleSelectContact(contact.id, checked as boolean);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="
                    border-2
                    bg-card
                    data-[state=checked]:bg-blue-600
                    data-[state=checked]:border-blue-600
                    data-[state=checked]:text-white
                  "
                />
              </div>
              <CardHeader className="pb-3 pl-12">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={contact.counselor.avatar} />
                      <AvatarFallback>
                        {contact.name.substring(0, 2)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <CardTitle className="text-base">{contact.name}</CardTitle>
                      <p className="text-sm text-muted-foreground">{contact.course}</p>
                    </div>
                  </div>
                  <Badge className={getPriorityColor(contact.priority)}>
                    {contact.priority}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Contact Info */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{contact.phone}</span>
                  </div>
                  {contact.alternatePhone && (
                    <div className="flex items-center gap-2 text-sm ml-6">
                      <span className="text-muted-foreground">{contact.alternatePhone}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground truncate">{contact.email}</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <span className="text-muted-foreground">
                      {contact.city}, {contact.state} - {contact.pincode}
                    </span>
                  </div>
                </div>

                {/* Rating */}
                <div className="flex items-center gap-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className={`h-4 w-4 ${
                        i < contact.rating
                          ? "fill-yellow-400 text-yellow-400"
                          : "text-gray-300"
                      }`}
                    />
                  ))}
                </div>

                {/* Last Contact */}
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  Last contact: {contact.lastContact}
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-3 gap-2 pt-2">
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => toast.success(`Calling ${contact.phone}`)}>
                    <Phone className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full">
                    <Mail className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => {
                    setSelectedLead(contact);
                    setMessageText("");
                    setIsMessageDialogOpen(true);
                  }}>
                    <MessageSquare className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => handleEditContact(contact)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Edit Contact Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="bg-white dark:bg-card text-black dark:text-white rounded-lg shadow-lg p-6 sm:max-w-[425px] border-slate-200 dark:border-slate-800">
          <DialogHeader>
            <DialogTitle>Edit Contact</DialogTitle>
            <DialogDescription>
              Make changes to the contact information below.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={editPhone}
                onChange={(e) => setEditPhone(e.target.value)}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="alternatePhone">Alternate Phone</Label>
              <Input
                id="alternatePhone"
                value={editAlternatePhone}
                onChange={(e) => setEditAlternatePhone(e.target.value)}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={editAddress}
                onChange={(e) => setEditAddress(e.target.value)}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                value={editCity}
                onChange={(e) => setEditCity(e.target.value)}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="state">State</Label>
              <Input
                id="state"
                value={editState}
                onChange={(e) => setEditState(e.target.value)}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="pincode">Pincode</Label>
              <Input
                id="pincode"
                value={editPincode}
                onChange={(e) => setEditPincode(e.target.value)}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="course">Course</Label>
              <Input
                id="course"
                value={editCourse}
                onChange={(e) => setEditCourse(e.target.value)}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="priority">Priority</Label>
              <Select
                value={editPriority}
                onValueChange={(value) => setEditPriority(value as "High" | "Medium" | "Low")}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="Low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button
  type="button"
  onClick={handleSaveEdit}
  className="bg-primary hover:bg-primary/90 text-white"
>
  Save changes
</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

     {/* Message Dialog */}
    <Dialog open={isMessageDialogOpen} onOpenChange={setIsMessageDialogOpen}>
      <DialogContent className="sm:max-w-[550px] bg-white dark:bg-card text-black dark:text-white rounded-lg shadow-lg p-6 border-slate-200 dark:border-slate-800">
        <DialogHeader>
          <DialogTitle>
            Message {selectedLead?.name}
          </DialogTitle>
          <DialogDescription>
            Send a message to {selectedLead?.name} at {selectedLead?.phone}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <Label>Phone Number</Label>
            <Input
              type="email"
              value={selectedLead?.phone || ""}
              readOnly
            />
          </div>

          <div className="space-y-2">
            <Label>Message</Label>
            <Textarea
              placeholder="Type your message here..."
              rows={3}
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setIsMessageDialogOpen(false)}
          >
            Cancel
          </Button>

          <Button
            className="bg-blue-600 hover:bg-blue-700"
            onClick={() => {
              if (!messageText.trim()) {
                toast.error("Message cannot be empty");
                return;
              }

              toast.success(`Message sent to ${selectedLead?.name}`);
              setIsMessageDialogOpen(false);
              setMessageText("");
            }}
          >
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

      {/* Communication Type Selection Dialog */}
      <Dialog open={isCommTypeDialogOpen} onOpenChange={setIsCommTypeDialogOpen}>
        <DialogContent className="sm:max-w-[500px] bg-card border-border rounded-2xl shadow-2xl p-0 overflow-hidden">
          <div className="p-6 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white">Choose Communication Channel</DialogTitle>
              <DialogDescription className="text-slate-500 dark:text-slate-400">
                Select how you want to reach out to {selectedContactIds.length} contact(s)
              </DialogDescription>
            </DialogHeader>
          </div>
          
          <div className="p-6 grid grid-cols-1 gap-4">
            <button
              onClick={() => handleSelectCommType("whatsapp")}
              className="group flex items-center gap-4 p-4 rounded-xl border-2 border-slate-100 dark:border-slate-800 hover:border-green-500/50 hover:bg-green-500/5 transition-all duration-200 text-left"
            >
              <div className="h-12 w-12 rounded-lg bg-green-500/10 flex items-center justify-center text-green-600 group-hover:scale-110 transition-transform">
                <MessageSquare className="h-6 w-6" />
              </div>
              <div>
                <p className="font-bold text-slate-900 dark:text-white">WhatsApp Business</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">Direct message to recipient's WhatsApp</p>
              </div>
              <ChevronRight className="ml-auto h-5 w-5 text-slate-300 group-hover:text-green-500 transition-colors" />
            </button>

            <button
              onClick={() => handleSelectCommType("message")}
              className="group flex items-center gap-4 p-4 rounded-xl border-2 border-slate-100 dark:border-slate-800 hover:border-blue-500/50 hover:bg-blue-500/5 transition-all duration-200 text-left"
            >
              <div className="h-12 w-12 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-600 group-hover:scale-110 transition-transform">
                <Send className="h-6 w-6" />
              </div>
              <div>
                <p className="font-bold text-slate-900 dark:text-white">SMS Message</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">Send text message via SMS gateway</p>
              </div>
              <ChevronRight className="ml-auto h-5 w-5 text-slate-300 group-hover:text-blue-500 transition-colors" />
            </button>

            <button
              onClick={() => handleSelectCommType("email")}
              className="group flex items-center gap-4 p-4 rounded-xl border-2 border-slate-100 dark:border-slate-800 hover:border-indigo-500/50 hover:bg-indigo-500/5 transition-all duration-200 text-left"
            >
              <div className="h-12 w-12 rounded-lg bg-indigo-500/10 flex items-center justify-center text-indigo-600 group-hover:scale-110 transition-transform">
                <Mail className="h-6 w-6" />
              </div>
              <div>
                <p className="font-bold text-slate-900 dark:text-white">Email Campaign</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">Reach inbox with professional email</p>
              </div>
              <ChevronRight className="ml-auto h-5 w-5 text-slate-300 group-hover:text-indigo-500 transition-colors" />
            </button>
          </div>
          
          <div className="p-4 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-100 dark:border-slate-800 flex justify-end">
            <Button variant="ghost" onClick={() => setIsCommTypeDialogOpen(false)} className="font-semibold text-slate-500 hover:text-slate-900">
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Communication Panel */}
      <BulkCommunicationPanel
        isOpen={isBulkCommunicationOpen}
        onClose={() => {
          setIsBulkCommunicationOpen(false);
          setSelectedCommType("email"); // Reset to default
        }}
        selectedContacts={selectedContacts}
        initialType={selectedCommType}
      />
    </div>
  );
}
